export interface NetworkLocation {
  latitude: number;
  longitude: number;
  altitude: number;
}

const REQUEST_TIMEOUT_MS = 1200;

const toFiniteNumber = (value: unknown): number | null => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const fetchWithTimeout = async (
  url: string,
  timeoutMs: number,
): Promise<Response> => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      method: "GET",
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timer);
  }
};

export const getNetworkLocationAsync = async (): Promise<NetworkLocation | null> => {
  if (typeof window === "undefined") return null;

  try {
    const ipapiResponse = await fetchWithTimeout(
      "https://ipapi.co/json/",
      REQUEST_TIMEOUT_MS,
    );

    if (ipapiResponse.ok) {
      const ipapiData = (await ipapiResponse.json()) as {
        latitude?: number;
        longitude?: number;
      };
      const latitude = toFiniteNumber(ipapiData.latitude);
      const longitude = toFiniteNumber(ipapiData.longitude);

      if (latitude !== null && longitude !== null) {
        return {
          latitude,
          longitude,
          altitude: 0,
        };
      }
    }
  } catch {
    // Fall back to secondary provider.
  }

  try {
    const ipwhoisResponse = await fetchWithTimeout(
      "https://ipwho.is/",
      REQUEST_TIMEOUT_MS,
    );

    if (ipwhoisResponse.ok) {
      const ipwhoisData = (await ipwhoisResponse.json()) as {
        latitude?: number;
        longitude?: number;
      };

      const latitude = toFiniteNumber(ipwhoisData.latitude);
      const longitude = toFiniteNumber(ipwhoisData.longitude);

      if (latitude !== null && longitude !== null) {
        return {
          latitude,
          longitude,
          altitude: 0,
        };
      }
    }
  } catch {
    // No-op: return null so caller can fall back.
  }

  return null;
};
